import { createClient } from "@/utils/supabase/client";
import { getUserSettings, updateUserSettings } from "@/app/[locale]/actions/settings";
import { generateUUID } from "./utils";

export interface HistoryEntry {
  id: string;
  sourceLanguageId: string;
  targetLanguageId: string;
  sourceText: string;
  targetText: string;
  createdAt: string;
  isFavorite: boolean;
  sourceLangName?: string;
  targetLangName?: string;
  targetVarietyId?: string;
  targetScriptId?: string;
  targetVarietyName?: string;
  targetScriptName?: string;
}

export interface HistorySettings {
  isEnabled: boolean;
}

const HISTORY_SETTINGS_KEY = 'translationHistory';
const DEFAULT_HISTORY_SETTINGS: HistorySettings = { isEnabled: true };

class HistoryService {
  private static instance: HistoryService;

  private constructor() { }

  static getInstance(): HistoryService {
    if (!HistoryService.instance) {
      HistoryService.instance = new HistoryService();
    }
    return HistoryService.instance;
  }

  private async getCurrentUserId(): Promise<string | null> {
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id || null;
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  private getStorageKey(userId: string): string {
    return `starky-translate-history-${userId}`;
  }

  async getHistorySettings(): Promise<HistorySettings> {
    try {
      const settings = await getUserSettings(HISTORY_SETTINGS_KEY);
      return { ...DEFAULT_HISTORY_SETTINGS, ...settings };
    } catch (error) {
      console.error('Failed to get history settings:', error);
      return DEFAULT_HISTORY_SETTINGS;
    }
  }

  async updateHistorySettings(settings: Partial<HistorySettings>): Promise<void> {
    try {
      await updateUserSettings(HISTORY_SETTINGS_KEY, settings);
    } catch (error) {
      console.error('Failed to update history settings:', error);
    }
  }

  async addHistoryEntry(entry: Omit<HistoryEntry, 'id' | 'createdAt' | 'isFavorite' | 'sourceLangName' | 'targetLangName' | 'targetVarietyName' | 'targetScriptName'>): Promise<void> {
    const userId = await this.getCurrentUserId();
    if (!userId) return;

    const settings = await this.getHistorySettings();
    if (!settings.isEnabled) return;

    const storageKey = this.getStorageKey(userId);
    const historyEntries = this.getHistoryFromStorage(storageKey);

    const newEntry: HistoryEntry = {
      ...entry,
      id: generateUUID(),
      createdAt: new Date().toISOString(),
      isFavorite: false
    };

    // Add to beginning of array (most recent first)
    historyEntries.unshift(newEntry);

    // Keep only last 100 entries to prevent localStorage from getting too large
    const limitedEntries = historyEntries.slice(0, 100);

    this.saveHistoryToStorage(storageKey, limitedEntries);
  }

  async getHistory(): Promise<HistoryEntry[]> {
    const userId = await this.getCurrentUserId();
    if (!userId) return [];

    const storageKey = this.getStorageKey(userId);
    return this.getHistoryFromStorage(storageKey);
  }

  async updateHistoryEntry(entryId: string, updates: Partial<HistoryEntry>): Promise<void> {
    const userId = await this.getCurrentUserId();
    if (!userId) return;

    const storageKey = this.getStorageKey(userId);
    const historyEntries = this.getHistoryFromStorage(storageKey);

    const entryIndex = historyEntries.findIndex(entry => entry.id === entryId);
    if (entryIndex === -1) return;

    historyEntries[entryIndex] = { ...historyEntries[entryIndex], ...updates };
    this.saveHistoryToStorage(storageKey, historyEntries);
  }

  async deleteHistoryEntry(entryId: string): Promise<void> {
    const userId = await this.getCurrentUserId();
    if (!userId) return;

    const storageKey = this.getStorageKey(userId);
    const historyEntries = this.getHistoryFromStorage(storageKey);

    const filteredEntries = historyEntries.filter(entry => entry.id !== entryId);
    this.saveHistoryToStorage(storageKey, filteredEntries);
  }

  async clearAllHistory(): Promise<void> {
    const userId = await this.getCurrentUserId();
    if (!userId) return;

    const storageKey = this.getStorageKey(userId);
    this.saveHistoryToStorage(storageKey, []);
  }

  async searchHistory(query: string): Promise<HistoryEntry[]> {
    const history = await this.getHistory();
    if (!query.trim()) return history;

    const lowercaseQuery = query.toLowerCase();
    return history.filter(entry =>
      entry.sourceText.toLowerCase().includes(lowercaseQuery) ||
      entry.targetText.toLowerCase().includes(lowercaseQuery)
    );
  }

  async getFavoriteHistory(): Promise<HistoryEntry[]> {
    const history = await this.getHistory();
    return history.filter(entry => entry.isFavorite);
  }

  private getHistoryFromStorage(storageKey: string): HistoryEntry[] {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to parse history from localStorage:', error);
      return [];
    }
  }

  private saveHistoryToStorage(storageKey: string, entries: HistoryEntry[]): void {
    try {
      localStorage.setItem(storageKey, JSON.stringify(entries));
    } catch (error) {
      console.error('Failed to save history to localStorage:', error);
    }
  }
}

export const historyService = HistoryService.getInstance();
